/**
 * Log transport interface and HTTP transport implementation.
 *
 * Transports receive structured log entries and deliver them somewhere.
 * The HTTP transport batches entries and flushes them to a backend endpoint,
 * with retry logic and offline buffering via localStorage.
 */

import type { LogEntry } from './types';

/** A transport accepts log entries for delivery */
export interface LogTransport {
  send(entry: LogEntry): void;
  flush(): Promise<void>;
}

// ─── HTTP Transport ──────────────────────────────────────────────────────────

export interface HttpTransportConfig {
  /** Backend endpoint (e.g. "https://auth.example.com/api/v1/client-logs") */
  endpoint: string;
  /** Function returning the current auth token (or null) */
  getToken?: () => string | null;
  /** App identifier sent as `source` field (e.g. "organization-admin") */
  source: string;
  /** Max entries per batch (default 25) */
  batchSize?: number;
  /** Flush interval in ms (default 5000) */
  flushIntervalMs?: number;
  /** Max retry attempts per batch (default 2) */
  maxRetries?: number;
  /** localStorage key for offline buffer (default "colix_log_buffer") */
  storageKey?: string;
}

const DEFAULT_BATCH_SIZE = 25;
const DEFAULT_FLUSH_INTERVAL = 5_000;
const DEFAULT_MAX_RETRIES = 2;
const DEFAULT_STORAGE_KEY = 'colix_log_buffer';
const MAX_STORAGE_ENTRIES = 200;

export class HttpTransport implements LogTransport {
  private queue: LogEntry[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private flushing = false;

  private readonly endpoint: string;
  private readonly getToken: () => string | null;
  private readonly source: string;
  private readonly batchSize: number;
  private readonly maxRetries: number;
  private readonly storageKey: string;

  constructor(config: HttpTransportConfig) {
    this.endpoint = config.endpoint;
    this.getToken = config.getToken ?? (() => null);
    this.source = config.source;
    this.batchSize = config.batchSize ?? DEFAULT_BATCH_SIZE;
    this.maxRetries = config.maxRetries ?? DEFAULT_MAX_RETRIES;
    this.storageKey = config.storageKey ?? DEFAULT_STORAGE_KEY;

    // Restore any buffered entries from a previous session
    this.restoreFromStorage();

    // Start periodic flush
    const interval = config.flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL;
    this.timer = setInterval(() => void this.flush(), interval);

    // Flush on page unload (best-effort via sendBeacon)
    if (typeof window !== 'undefined') {
      window.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.flushSync();
        }
      });
    }
  }

  /** Enqueue a log entry. Triggers flush when batch is full. */
  send(entry: LogEntry): void {
    this.queue.push(entry);
    if (this.queue.length >= this.batchSize) {
      void this.flush();
    }
  }

  /** Flush all queued entries to the backend */
  async flush(): Promise<void> {
    if (this.flushing || this.queue.length === 0) return;
    this.flushing = true;

    const batch = this.queue.splice(0, this.batchSize);

    try {
      await this.sendBatch(batch);
    } catch {
      // Send failed after retries — persist to localStorage for next session
      this.persistToStorage(batch);
    } finally {
      this.flushing = false;
      // If there are still entries queued, schedule another flush
      if (this.queue.length >= this.batchSize) {
        void this.flush();
      }
    }
  }

  /** Stop the transport (clears interval, persists remaining entries) */
  stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (this.queue.length > 0) {
      this.persistToStorage(this.queue.splice(0));
    }
  }

  // ── Internal ─────────────────────────────────────────────────────────────

  private async sendBatch(entries: LogEntry[], attempt = 0): Promise<void> {
    const token = this.getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const body = JSON.stringify({ source: this.source, entries });

    const response = await fetch(this.endpoint, {
      method: 'POST',
      headers,
      body,
      keepalive: true, // allow send during page unload
    });

    if (!response.ok) {
      if (attempt < this.maxRetries && response.status >= 500) {
        // Exponential backoff: 1s, 2s
        await sleep(1000 * (attempt + 1));
        return this.sendBatch(entries, attempt + 1);
      }
      throw new Error(`Client log flush failed: ${response.status}`);
    }
  }

  /** Best-effort synchronous flush via sendBeacon (for page unload) */
  private flushSync(): void {
    if (this.queue.length === 0) return;

    const batch = this.queue.splice(0, this.batchSize);
    const body = JSON.stringify({ source: this.source, entries: batch });

    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const sent = navigator.sendBeacon(
        this.endpoint,
        new Blob([body], { type: 'application/json' })
      );
      if (!sent) {
        this.persistToStorage(batch);
      }
    } else {
      this.persistToStorage(batch);
    }
  }

  private persistToStorage(entries: LogEntry[]): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const existing = this.readStorage();
      // Keep total under limit — drop oldest if overflow
      const combined = [...existing, ...entries].slice(-MAX_STORAGE_ENTRIES);
      localStorage.setItem(this.storageKey, JSON.stringify(combined));
    } catch {
      // localStorage full or unavailable — drop silently
    }
  }

  private restoreFromStorage(): void {
    const entries = this.readStorage();
    if (entries.length > 0) {
      this.queue.push(...entries);
      try {
        localStorage.removeItem(this.storageKey);
      } catch {
        // ignore
      }
    }
  }

  private readStorage(): LogEntry[] {
    if (typeof localStorage === 'undefined') return [];
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
